// frontend/src/pages/ProductDetailPage.tsx
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, ShoppingBag, CalendarDays, Edit, Loader2, AlertTriangle, Info } from 'lucide-react';
import { getProductByIdOrSlug } from '@/api/productService';
import { type Product } from '@/types';

const ProductDetailPage = () => {
  const { productIdOrSlug } = useParams<{ productIdOrSlug: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const backendBaseUrl = import.meta.env.VITE_API_BASE_URL?.replace('/api/v1', '') || 'http://localhost:8000';

  useEffect(() => {
    if (!productIdOrSlug) {
      setError("Product identifier is missing from URL.");
      setIsLoading(false);
      return;
    }
    const fetchProductDetails = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await getProductByIdOrSlug(productIdOrSlug);
        setProduct(data);
      } catch (err: any) {
        console.error("Failed to fetch product details:", err);
        if (err.response && err.response.status === 404) {
          setError("Product not found.");
        } else {
          setError("Failed to load product details. Please try again later.");
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchProductDetails();
  }, [productIdOrSlug]);

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  };

  const getFullImageUrl = (relativePath: string | null | undefined): string | undefined => {
    if (!relativePath) return undefined;
    if (relativePath.startsWith('http')) return relativePath;
    return `${backendBaseUrl}/static/${relativePath.startsWith('/') ? relativePath.substring(1) : relativePath}`;
  };
  
  // Function to safely render HTML content (if 'details' field contains HTML)
  // Important: Ensure HTML from backend is sanitized to prevent XSS if it's user-generated.
  // For trusted admin-generated content, this is generally okay.
  const renderHtmlDetails = (htmlString?: string | null) => {
    if (!htmlString) return <p className="text-muted-foreground">No detailed information available.</p>;
    // Using prose class for Tailwind Typography styling
    return <div className="prose prose-lg dark:prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: htmlString }} />;
  };


  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-20 flex flex-col items-center justify-center min-h-[60vh]">
        <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
        <p className="text-lg text-muted-foreground">Loading product details...</p>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="container mx-auto px-4 py-20 text-center min-h-[60vh] flex flex-col justify-center items-center">
        <AlertTriangle className="h-16 w-16 text-destructive mx-auto mb-6" />
        <h1 className="text-3xl font-bold mb-3 text-foreground">{error || "Product Not Found"}</h1>
        <p className="text-muted-foreground mb-8 max-w-md">
          {error ? "There was an issue loading the product." : "The product you are looking for does not exist or may have been moved."}
        </p>
        <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary/10 hover:text-primary transition-colors">
          <Link to="/products"><ArrowLeft className="mr-2 h-4 w-4" /> Back to Products</Link>
        </Button>
      </div>
    );
  }
  
  const fullImageUrl = getFullImageUrl(product.image_url);

  return (
    <div className="bg-gradient-to-br from-background via-muted/30 to-background dark:from-background dark:via-muted/10 dark:to-background py-8 md:py-12 text-foreground">
      <div className="container mx-auto max-w-4xl">
        <div className="mb-8">
          <Button asChild variant="ghost" className="text-primary hover:text-primary/80 hover:bg-primary/5 dark:text-accent dark:hover:text-accent/80 dark:hover:bg-accent/10 transition-colors px-2">
            <Link to="/products"><ArrowLeft className="mr-2 h-4 w-4" /> Back to All Products</Link>
          </Button>
        </div>

        <article className="bg-card p-6 md:p-10 rounded-xl shadow-2xl border border-border/30">
          <header className="mb-4 border-b border-border/50 pb-8">
            <div className="flex flex-col md:flex-row md:items-start gap-6">
                {fullImageUrl && (
                    <div className="w-full md:w-1/3 flex-shrink-0">
                    <img
                        src={fullImageUrl}
                        alt={product.name}
                        className="w-full h-auto object-contain rounded-lg shadow-md border border-border/20 max-h-[300px] md:max-h-none"
                    />
                    </div>
                )}
                 {!fullImageUrl && (
                    <div className="w-full md:w-1/3 flex-shrink-0 h-48 bg-muted/50 dark:bg-muted/20 flex items-center justify-center rounded-lg border border-border/20">
                        <ShoppingBag className="h-24 w-24 text-muted-foreground/50" />
                    </div>
                )}
                <div className="flex-grow">
                    <Badge variant="secondary" className="mb-3 bg-primary/10 text-primary dark:bg-accent/10 dark:text-accent">{product.status.replace('_', ' ').toUpperCase()}</Badge>
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-3 leading-tight text-gradient-maroon-to-gold">
                        {product.name}
                    </h1>
                    {product.description && (
                        <p className="text-lg md:text-xl text-muted-foreground italic">
                        {product.description}
                        </p>
                    )}
                     <div className="mt-4 text-sm text-muted-foreground space-y-1">

                        <div className="flex items-center">
                            <CalendarDays className="h-4 w-4 mr-2 text-primary/70" /> <span>Last Updated: {formatDate(product.updated_at)}</span>
                        </div>
                        {product.creator && (
                             <div className="flex items-center">
                                <Edit className="h-4 w-4 mr-2 text-primary/70" /> <span>Added by: {product.creator.name || product.creator.email}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
          </header>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4 text-foreground border-b border-border/40 pb-4">Product Details</h2>
            {renderHtmlDetails(product.details)}
          </section>

          {/* You can add more sections like Features, Specifications, Pricing, etc. */}

          <footer className="mt-10 pt-8 border-t border-border/50 text-center">
             <p className="text-sm text-muted-foreground mb-4">Interested in {product.name}? Contact us for more information or a demo.</p>
            <Button size="lg" asChild className="!bg-gradient-to-r !from-primary !to-accent hover:!from-primary/90 hover:!to-accent/90 text-primary-foreground shadow-lg hover:shadow-xl">
                <Link to="/contact">Get in Touch</Link>
            </Button>
          </footer>
        </article>
      </div>
    </div>
  );
};

export default ProductDetailPage;